import React, { useEffect, useRef, useState, useCallback } from 'react'
import * as THREE from 'three'
import { SoundToggleBtn } from '../ui/SoundToggleBtn'
import { fmtCoins } from '../../lib/cards'
import { io, Socket } from 'socket.io-client'
import { Sounds } from '../../lib/sounds'

// ─── European roulette wheel order (clockwise from 0) ─────────────────────────
const WHEEL_NUMS = [0,32,15,19,4,21,2,25,17,34,6,27,13,36,11,30,8,23,10,5,24,16,33,1,20,14,31,9,22,18,29,7,28,12,35,3,26]
const RED_NUMS   = new Set([1,3,5,7,9,12,14,16,18,19,21,23,25,27,30,32,34,36])
const SEG        = (2*Math.PI)/37

const numColor = (n:number) => n===0?'green':RED_NUMS.has(n)?'red':'black'

// ─── Canvas: top-down wheel texture ──────────────────────────────────────────
function makeWheelTex(): THREE.CanvasTexture {
  const S=1024, cx=S/2, cy=S/2
  const cvs=document.createElement('canvas'); cvs.width=S; cvs.height=S
  const ctx=cvs.getContext('2d')!

  // Dark base
  ctx.fillStyle='#1a0a00'; ctx.fillRect(0,0,S,S)

  WHEEL_NUMS.forEach((num,i)=>{
    const a0 = i*SEG - Math.PI/2
    const a1 = (i+1)*SEG - Math.PI/2
    const r  = S*0.465

    // Sector
    ctx.beginPath(); ctx.moveTo(cx,cy)
    ctx.arc(cx,cy,r,a0,a1); ctx.closePath()
    ctx.fillStyle = num===0?'#15a030':RED_NUMS.has(num)?'#cc1830':'#222222'
    ctx.fill()

    // Divider lines
    ctx.beginPath(); ctx.moveTo(cx,cy)
    ctx.lineTo(cx+r*Math.cos(a0), cy+r*Math.sin(a0))
    ctx.strokeStyle='#c8a020'; ctx.lineWidth=2; ctx.stroke()

    // Number text
    const ma=(a0+a1)/2, nr=r*0.74
    ctx.save()
    ctx.translate(cx+nr*Math.cos(ma), cy+nr*Math.sin(ma))
    ctx.rotate(ma+Math.PI/2)
    ctx.fillStyle='#ffffff'
    ctx.font=`bold ${num>9?20:22}px Arial`
    ctx.textAlign='center'; ctx.textBaseline='middle'
    ctx.shadowColor='rgba(0,0,0,0.8)'; ctx.shadowBlur=4
    ctx.fillText(String(num),0,0)
    ctx.restore()
  })

  // Outer ring (gold)
  ctx.beginPath(); ctx.arc(cx,cy,S*0.48,0,Math.PI*2)
  ctx.strokeStyle='#c8a020'; ctx.lineWidth=10; ctx.stroke()

  // Inner decorative ring
  ctx.beginPath(); ctx.arc(cx,cy,S*0.30,0,Math.PI*2)
  ctx.strokeStyle='#c8a020'; ctx.lineWidth=4; ctx.stroke()

  // Centre wood panel
  const wg=ctx.createRadialGradient(cx,cy,0,cx,cy,S*0.28)
  wg.addColorStop(0,'#7a3a18'); wg.addColorStop(0.6,'#5a2a10'); wg.addColorStop(1,'#3a1a08')
  ctx.beginPath(); ctx.arc(cx,cy,S*0.28,0,Math.PI*2)
  ctx.fillStyle=wg; ctx.fill()

  // Centre gold disc
  const gg=ctx.createRadialGradient(cx-10,cy-10,2,cx,cy,S*0.07)
  gg.addColorStop(0,'#ffe080'); gg.addColorStop(1,'#a07010')
  ctx.beginPath(); ctx.arc(cx,cy,S*0.065,0,Math.PI*2)
  ctx.fillStyle=gg; ctx.fill()

  return new THREE.CanvasTexture(cvs)
}

// ─── Canvas: green felt betting table ─────────────────────────────────────────
function makeFeltTex(): THREE.CanvasTexture {
  const S=512; const cvs=document.createElement('canvas'); cvs.width=S; cvs.height=S
  const ctx=cvs.getContext('2d')!
  const g=ctx.createRadialGradient(S/2,S/2,0,S/2,S/2,S*.7)
  g.addColorStop(0,'#2a9b52'); g.addColorStop(0.5,'#1e7a40'); g.addColorStop(1,'#155c2e')
  ctx.fillStyle=g; ctx.fillRect(0,0,S,S)
  for(let i=0;i<5000;i++){
    ctx.fillStyle=`rgba(${Math.random()>0.5?200:0},${Math.random()>0.5?80:0},0,0.02)`
    ctx.fillRect(Math.random()*S,Math.random()*S,1,1)
  }
  return new THREE.CanvasTexture(cvs)
}

// ─── Types ────────────────────────────────────────────────────────────────────
interface Props { token:string; balance:number|null; playerId?:string; operatorId?:string }
type Phase    = 'idle'|'spinning'|'result'
type BetType  = 'red'|'black'|'dozen1'|'dozen2'|'dozen3'|'straight'
interface PlacedBet { type:BetType; value?:number; stake:number; label:string }

const BET_PAYOUT: Record<BetType,number> = {
  red:2, black:2, dozen1:3, dozen2:3, dozen3:3, straight:36
}

// ─── Main component ───────────────────────────────────────────────────────────
export function RouletteGame({token,balance:initBalance,playerId:initPid,operatorId:initOid}:Props){
  const mountRef   = useRef<HTMLDivElement>(null)
  const socketRef  = useRef<Socket|null>(null)
  const sessionRef = useRef({playerId:initPid||'',operatorId:initOid||''})

  // Three.js refs
  const rendRef    = useRef<THREE.WebGLRenderer|null>(null)
  const sceneRef   = useRef<THREE.Scene|null>(null)
  const camRef     = useRef<THREE.PerspectiveCamera|null>(null)
  const wheelRef   = useRef<THREE.Group|null>(null)
  const ballRef    = useRef<THREE.Mesh|null>(null)
  const rafRef     = useRef(0)

  const anim = useRef({
    wheelSpeed: 0,      // rad/s
    wheelAngle: 0,      // cumulative angle
    ballAngle:  0,      // ball orbital angle
    ballRadius: 4.2,    // distance from centre
    ballHeight: 0.35,   // Y position
    phase: 'idle' as 'idle'|'spinning'|'settling'|'done',
    targetAngle:0,      // final ball angle
    t: 0,
    dur: 4.0,
  })

  // React state
  const [phase,setPhase]       = useState<Phase>('idle')
  const [balance,setBalance]   = useState<number|null>(initBalance)
  const [bets,setBets]         = useState<PlacedBet[]>([])
  const [activeBetType,setActiveBetType] = useState<BetType>('red')
  const [straightNum,setStraightNum]     = useState(0)
  const [stake,setStake]       = useState(20)
  const [result,setResult]     = useState<{number:number;color:string;totalWin:number}|null>(null)
  const [winNum,setWinNum]     = useState<number|null>(null)
  const [toast,setToast]       = useState('')
  const [showNumPad,setShowNumPad] = useState(false)
  const [history,setHistory]   = useState<Array<{n:number;c:string}>>([])
  const [currency,setCurrency] = useState('coins')

  useEffect(()=>{ if(initBalance!==null) setBalance(initBalance) },[initBalance])
  const showToast=(msg:string,dur=2500)=>{ setToast(msg); setTimeout(()=>setToast(''),dur) }

  // Total stake
  const totalStake = bets.reduce((s,b)=>s+b.stake,0)

  // Add bet
  const addBet = () => {
    if(phase!=='idle'&&phase!=='result') return
    const label = activeBetType==='straight' ? `#${straightNum}`
      : activeBetType==='red' ? 'Red'
      : activeBetType==='black' ? 'Black'
      : activeBetType==='dozen1' ? '1-12'
      : activeBetType==='dozen2' ? '13-24' : '25-36'
    const newBet:PlacedBet = {
      type:activeBetType,
      value:activeBetType==='straight'?straightNum:undefined,
      stake, label,
    }
    // Replace same type+value
    setBets(prev=>{
      const filtered=prev.filter(b=>!(b.type===newBet.type&&b.value===newBet.value))
      return [...filtered, newBet]
    })
  }
  const clearBets = () => setBets([])

  // ── Three.js setup ──────────────────────────────────────────────────────────
  useEffect(()=>{
    const el=mountRef.current; if(!el) return
    const W=el.clientWidth, H=el.clientHeight

    const renderer=new THREE.WebGLRenderer({antialias:true})
    renderer.setSize(W,H); renderer.setPixelRatio(Math.min(window.devicePixelRatio,2))
    renderer.shadowMap.enabled=true; renderer.shadowMap.type=THREE.PCFSoftShadowMap
    renderer.setClearColor(0x0f1f12)
    el.appendChild(renderer.domElement); rendRef.current=renderer

    const scene=new THREE.Scene()
    scene.fog=new THREE.FogExp2(0x0a1a0a,0.06); sceneRef.current=scene

    // Camera — cinematic angled view
    const cam=new THREE.PerspectiveCamera(45,W/H,0.1,100)
    cam.position.set(0,7,9); cam.lookAt(0,0,0); camRef.current=cam

    // Lights
    scene.add(new THREE.AmbientLight(0xffffff,1.8))
    const spot=new THREE.SpotLight(0xffffff,6.0,30,Math.PI/4,0.3)
    spot.position.set(0,10,4); spot.castShadow=true
    spot.shadow.mapSize.set(2048,2048); scene.add(spot)
    const fill1=new THREE.DirectionalLight(0xffffff,1.5)
    fill1.position.set(-5,4,3); scene.add(fill1)
    const fill2=new THREE.DirectionalLight(0xfff0d0,1.2)
    fill2.position.set(5,3,-3); scene.add(fill2)
    const fill3=new THREE.DirectionalLight(0xe0f0ff,0.8)
    fill3.position.set(0,2,8); scene.add(fill3)

    // Table surface
    const felt=makeFeltTex(); felt.wrapS=felt.wrapT=THREE.RepeatWrapping; felt.repeat.set(3,3)
    const tableGeo=new THREE.CylinderGeometry(9,9,0.15,64)
    const table=new THREE.Mesh(tableGeo,new THREE.MeshStandardMaterial({map:felt,roughness:.95}))
    table.receiveShadow=true; table.position.y=-0.3; scene.add(table)

    // Table rim
    const rimMesh=new THREE.Mesh(
      new THREE.TorusGeometry(9,0.28,12,64),
      new THREE.MeshStandardMaterial({color:0x5a2a10,roughness:.7,metalness:.1})
    )
    rimMesh.rotation.x=Math.PI/2; rimMesh.position.y=-0.15; scene.add(rimMesh)

    // ── Roulette wheel group ─────────────────────────────────────────────────
    const wheelGroup=new THREE.Group(); scene.add(wheelGroup); wheelRef.current=wheelGroup

    // Outer chrome bowl
    const bowlGeo=new THREE.CylinderGeometry(4.4,4.6,0.55,64,1,true)
    const bowlMat=new THREE.MeshStandardMaterial({color:0xc8a020,roughness:.3,metalness:.7,side:THREE.BackSide})
    wheelGroup.add(new THREE.Mesh(bowlGeo,bowlMat))

    // Outer rim top ring
    const rimTop=new THREE.Mesh(
      new THREE.TorusGeometry(4.5,0.18,12,64),
      new THREE.MeshStandardMaterial({color:0xd4b030,roughness:.25,metalness:.8})
    )
    rimTop.rotation.x=Math.PI/2; rimTop.position.y=0.28; wheelGroup.add(rimTop)

    // Ball track ring
    const trackMesh=new THREE.Mesh(
      new THREE.TorusGeometry(4.0,0.08,8,64),
      new THREE.MeshStandardMaterial({color:0xa08010,roughness:.4,metalness:.6})
    )
    trackMesh.rotation.x=Math.PI/2; trackMesh.position.y=0.2; wheelGroup.add(trackMesh)

    // Spinning wheel face (canvas texture)
    const wheelFace=new THREE.Mesh(
      new THREE.CylinderGeometry(3.9,3.9,0.18,64),
      [
        new THREE.MeshStandardMaterial({color:0x2a1008,roughness:.8}), // side
        new THREE.MeshStandardMaterial({map:makeWheelTex(),roughness:.3,metalness:0.05}), // top
        new THREE.MeshStandardMaterial({color:0x1a0808,roughness:.9}), // bottom
      ]
    )
    wheelFace.castShadow=true; wheelGroup.add(wheelFace)

    // Centre diamond posts (decorative)
    for(let i=0;i<8;i++){
      const angle=i*(Math.PI/4)
      const post=new THREE.Mesh(
        new THREE.CylinderGeometry(0.04,0.04,0.25,8),
        new THREE.MeshStandardMaterial({color:0xd4b030,metalness:.9,roughness:.2})
      )
      post.position.set(Math.cos(angle)*3.1,0.2,Math.sin(angle)*3.1)
      wheelGroup.add(post)
    }

    // Centre cone
    const cone=new THREE.Mesh(
      new THREE.ConeGeometry(0.45,0.55,32),
      new THREE.MeshStandardMaterial({color:0xc8a020,metalness:.8,roughness:.2})
    )
    cone.position.y=0.36; wheelGroup.add(cone)

    // Ball
    const ball=new THREE.Mesh(
      new THREE.SphereGeometry(0.13,16,16),
      new THREE.MeshStandardMaterial({color:0xf0f0f0,roughness:.1,metalness:.05})
    )
    ball.castShadow=true; scene.add(ball); ballRef.current=ball
    ball.position.set(4.0,0.35,0); ball.visible=false

    // Resize handler
    const onResize=()=>{
      const w=el.clientWidth,h=el.clientHeight
      renderer.setSize(w,h); cam.aspect=w/h; cam.updateProjectionMatrix()
    }
    window.addEventListener('resize',onResize)

    // Render loop
    let last=performance.now()
    const loop=(now:number)=>{
      rafRef.current=requestAnimationFrame(loop)
      const dt=Math.min((now-last)/1000,0.05); last=now
      const A=anim.current

      if(A.phase==='spinning'){
        // Wheel accelerates then maintains speed
        A.wheelSpeed=Math.min(A.wheelSpeed+dt*1.5, 3.5)
        A.wheelAngle+=A.wheelSpeed*dt
        wheelGroup.rotation.y=A.wheelAngle

        // Ball orbits opposite at higher speed
        A.ballAngle-=(A.wheelSpeed*1.6)*dt
        ball.position.x=A.ballRadius*Math.cos(A.ballAngle)
        ball.position.z=A.ballRadius*Math.sin(A.ballAngle)
        ball.position.y=A.ballHeight+Math.sin(A.ballAngle*3)*0.04

        A.t+=dt
        if(A.t>=A.dur) A.phase='settling'

      } else if(A.phase==='settling'){
        // Slow wheel
        A.wheelSpeed=Math.max(A.wheelSpeed-dt*1.2,0.3)
        A.wheelAngle+=A.wheelSpeed*dt
        wheelGroup.rotation.y=A.wheelAngle

        // Ball spirals inward and slows
        const progress=Math.min(A.t/1.5,1)
        A.ballRadius=4.0-progress*0.5  // spiral inward
        A.ballHeight=0.35-progress*0.18 // drop

        // Approach target angle
        const diff=((A.targetAngle-A.ballAngle)%(Math.PI*2)+Math.PI*3)%(Math.PI*2)-Math.PI
        A.ballAngle+=diff*(dt*2.5)
        A.ballAngle-=A.wheelSpeed*0.5*dt

        ball.position.x=A.ballRadius*Math.cos(A.ballAngle)
        ball.position.z=A.ballRadius*Math.sin(A.ballAngle)
        ball.position.y=A.ballHeight+Math.sin(progress*Math.PI)*0.08  // bounce

        A.t+=dt
        if(A.t>=1.5){ A.phase='done'; A.wheelSpeed=0.15 }

      } else if(A.phase==='done'){
        // Slow drift
        A.wheelAngle+=0.15*dt
        wheelGroup.rotation.y=A.wheelAngle
        ball.position.x=A.ballRadius*Math.cos(A.ballAngle)
        ball.position.z=A.ballRadius*Math.sin(A.ballAngle)

      } else {
        // Idle: slow drift
        A.wheelAngle+=0.4*dt
        wheelGroup.rotation.y=A.wheelAngle
      }

      renderer.render(scene,cam)
    }
    rafRef.current=requestAnimationFrame(loop)

    return()=>{
      window.removeEventListener('resize',onResize)
      cancelAnimationFrame(rafRef.current)
      renderer.dispose(); el.removeChild(renderer.domElement)
    }
  },[])

  // ── Socket ──────────────────────────────────────────────────────────────────
  useEffect(()=>{
    const GS=(import.meta as any).env?.VITE_GAME_SERVER_URL||'http://localhost:3002'
    const s=io(GS,{transports:['polling'],auth:{token}})
    socketRef.current=s
    try{
      const p=JSON.parse(atob(token.split('.')[1]))
      sessionRef.current={playerId:p.sub||'',operatorId:p.operator_id||''}
      if(p.currency) setCurrency(p.currency)
    }catch{}

    s.on('connect',async()=>{
      try{
        const {playerId}=sessionRef.current
        const r=await fetch(`${GS}/wallet/balance?player_id=${playerId}`)
        const d=await r.json(); if(typeof d.balance==='number') setBalance(d.balance)
      }catch{}
    })

    s.on('roulette:result',(d:any)=>{
      const {number,color,total_win,balance:newBal,bets_result} = d
      const A=anim.current

      // Calculate ball target angle based on winning number position
      const numIdx   = WHEEL_NUMS.indexOf(number)
      const pocketAngle = numIdx * SEG
      // Ball target = wheel's final angle + pocket offset (opposite spin direction)
      A.targetAngle  = -(A.wheelAngle + pocketAngle + Math.PI/2)
      A.phase        = 'settling'
      A.t            = 0

      // After settling show result
      setTimeout(()=>{
        setWinNum(number)
        setResult({number,color,totalWin:total_win})
        if(typeof newBal==='number') setBalance(newBal)
        setHistory(h=>[{n:number,c:color},...h].slice(0,12))
        setPhase('result')
        if(total_win>0){
          Sounds.cashout()
          showToast(`🎉 ${number} ${color.toUpperCase()}! WIN ₹${total_win.toFixed(2)}`,3000)
        } else {
          Sounds.cooked()
          showToast(`${number} ${color} — No win`,2000)
        }
      },1800)
    })

    s.on('roulette:error',(d:any)=>{ showToast(`❌ ${d.error}`); setPhase('idle') })
    s.on('balance',(d:any)=>{ if(typeof d.balance==='number') setBalance(d.balance) })
    return()=>{ s.disconnect() }
  },[token])

  // ── Spin ────────────────────────────────────────────────────────────────────
  const handleSpin=()=>{
    if(phase==='spinning') return
    if(bets.length===0) return showToast('Place a bet first!')
    if(balance!==null&&balance<totalStake) return showToast('Not enough balance')

    const s=socketRef.current; const {playerId,operatorId}=sessionRef.current
    if(!s) return

    setBalance(b=>b!==null?Math.max(0,b-totalStake):b)
    setPhase('spinning'); setResult(null); setWinNum(null)
    Sounds.go()

    // Start 3D animation
    const A=anim.current
    A.phase='spinning'; A.t=0; A.dur=3.5
    A.wheelSpeed=0; A.ballRadius=4.0; A.ballHeight=0.35
    if(ballRef.current) ballRef.current.visible=true

    s.emit('roulette:spin',{
      player_id:playerId, operator_id:operatorId,
      bets:bets.map(b=>({type:b.type,value:b.value,stake:b.stake}))
    })
  }

  const canSpin = phase==='idle'||phase==='result'
  const STAKES  = [5,10,20,50,100,200,500]

  const BET_BUTTONS = [
    {type:'red'   as BetType, label:'RED',   bg:'#b01020', color:'#fff'},
    {type:'black' as BetType, label:'BLACK',  bg:'#111',    color:'#fff', border:'rgba(255,255,255,.25)'},
    {type:'dozen1'as BetType, label:'1-12',   bg:'rgba(255,255,255,.08)', color:'rgba(255,255,255,.7)'},
    {type:'dozen2'as BetType, label:'13-24',  bg:'rgba(255,255,255,.08)', color:'rgba(255,255,255,.7)'},
    {type:'dozen3'as BetType, label:'25-36',  bg:'rgba(255,255,255,.08)', color:'rgba(255,255,255,.7)'},
    {type:'straight'as BetType,label:'STRAIGHT',bg:'rgba(201,168,76,.15)',color:'#c9a84c'},
  ]

  return(
    <div style={{display:'flex',flexDirection:'column',height:'100%',
      background:'#080f08',color:'#fff',fontFamily:'system-ui,sans-serif',
      userSelect:'none',overflow:'hidden',position:'relative'}}>
      <style>{`
        @keyframes rToast{0%{opacity:0;transform:translateX(-50%) scale(.85)}15%,85%{opacity:1;transform:translateX(-50%) scale(1)}100%{opacity:0}}
        @keyframes rPop{0%{transform:scale(.5);opacity:0}70%{transform:scale(1.15)}100%{transform:scale(1);opacity:1}}
        @keyframes rPulse{0%,100%{opacity:1}50%{opacity:.5}}
        .rb:hover{filter:brightness(1.2)!important}.rb:active{transform:scale(.95)!important}
      `}</style>

      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        padding:'6px 12px',background:'rgba(0,0,0,.6)',
        borderBottom:'1px solid rgba(201,168,76,.15)',flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:16}}>🎡</span>
          <strong style={{fontSize:12,letterSpacing:.8,color:'#c9a84c'}}>ROULETTE</strong>
          <span style={{fontSize:9,color:'rgba(255,255,255,.3)'}}>European • Single Zero</span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <SoundToggleBtn/>
          <div style={{background:'rgba(201,168,76,.1)',border:'1px solid rgba(201,168,76,.3)',
            borderRadius:7,padding:'3px 9px',fontWeight:700,color:'#c9a84c',fontSize:12}}>
            {balance!==null?`₹${fmtCoins(balance)}`:'—'}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast&&(
        <div style={{position:'absolute',top:52,left:'50%',zIndex:60,
          animation:'rToast 3s ease-in-out forwards',
          background:'rgba(0,0,0,.92)',color:'#c9a84c',padding:'8px 20px',
          borderRadius:20,fontSize:14,fontWeight:800,whiteSpace:'nowrap',
          border:'1px solid rgba(201,168,76,.4)'}}>
          {toast}
        </div>
      )}

      {/* Three.js canvas */}
      <div ref={mountRef} style={{flex:1,position:'relative',minHeight:0}}>

        {/* Win number overlay */}
        {winNum!==null&&result&&(
          <div style={{position:'absolute',top:10,left:'50%',transform:'translateX(-50%)',
            zIndex:10,animation:'rPop .5s ease-out',textAlign:'center',
            background:'rgba(0,0,0,.85)',borderRadius:16,padding:'8px 24px',
            border:`2px solid ${result.color==='red'?'#ef4444':result.color==='black'?'rgba(255,255,255,.3)':'#22c55e'}`}}>
            <div style={{fontSize:28,fontWeight:900,
              color:result.color==='red'?'#f87171':result.color==='black'?'#e5e7eb':'#4ade80'}}>
              {winNum}
            </div>
            <div style={{fontSize:10,color:'rgba(255,255,255,.5)',textTransform:'uppercase'}}>
              {result.color} {result.totalWin>0&&`• WIN ₹${result.totalWin.toFixed(2)}`}
            </div>
          </div>
        )}

        {/* History row */}
        <div style={{position:'absolute',bottom:6,left:'50%',transform:'translateX(-50%)',
          display:'flex',gap:3,zIndex:10}}>
          {history.slice(0,10).map((h,i)=>(
            <div key={i} style={{
              width:18,height:18,borderRadius:'50%',
              background:h.c==='red'?'#b01020':h.c==='black'?'#222':'#166534',
              border:'1px solid rgba(255,255,255,.2)',
              display:'flex',alignItems:'center',justifyContent:'center',
              fontSize:8,fontWeight:700,color:'#fff',opacity:1-i*0.08,
            }}>{h.n}</div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div style={{background:'linear-gradient(to top,rgba(0,0,0,.98),rgba(0,0,0,.75))',
        borderTop:'1px solid rgba(201,168,76,.1)',
        padding:'7px 10px 11px',flexShrink:0}}>

        {/* Active bets chips */}
        {bets.length>0&&(
          <div style={{display:'flex',gap:4,flexWrap:'wrap',marginBottom:6,alignItems:'center'}}>
            <span style={{fontSize:9,color:'rgba(255,255,255,.3)'}}>BETS:</span>
            {bets.map((b,i)=>(
              <div key={i} onClick={()=>setBets(p=>p.filter((_,j)=>j!==i))}
                style={{cursor:'pointer',background:'rgba(201,168,76,.2)',
                  border:'1px solid rgba(201,168,76,.4)',borderRadius:10,
                  padding:'1px 7px',fontSize:10,fontWeight:700,color:'#c9a84c'}}>
                {b.label} ₹{b.stake}×{BET_PAYOUT[b.type]}
              </div>
            ))}
            <span onClick={clearBets}
              style={{cursor:'pointer',fontSize:9,color:'rgba(239,68,68,.6)',marginLeft:2}}>
              ✕ clear
            </span>
          </div>
        )}

        {/* Bet type buttons */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:3,marginBottom:6}}>
          {BET_BUTTONS.map(b=>(
            <button key={b.type} className="rb"
              onClick={()=>{ setActiveBetType(b.type); setShowNumPad(b.type==='straight') }}
              style={{
                borderRadius:6,padding:'5px 2px',cursor:'pointer',
                border:activeBetType===b.type?'2px solid #c9a84c':'2px solid rgba(255,255,255,.1)',
                background:activeBetType===b.type?b.bg||'rgba(201,168,76,.2)':b.bg||'rgba(255,255,255,.05)',
                color:b.color||'#fff',fontSize:9,fontWeight:800,
                transition:'all .12s',lineHeight:1.3,
              }}>
              {b.label}
            </button>
          ))}
        </div>

        {/* Number pad for straight bet */}
        {showNumPad&&activeBetType==='straight'&&(
          <div style={{display:'flex',flexWrap:'wrap',gap:2,marginBottom:6,maxHeight:72,overflowY:'auto'}}>
            {Array.from({length:37},(_,i)=>(
              <button key={i} onClick={()=>setStraightNum(i)}
                style={{
                  width:26,height:22,borderRadius:4,cursor:'pointer',
                  border:straightNum===i?'2px solid #fff':'1px solid rgba(255,255,255,.15)',
                  background:i===0?'#166534':RED_NUMS.has(i)?'#b01020':'#111',
                  color:'#fff',fontSize:9,fontWeight:700,
                  outline:straightNum===i?'none':'none',
                }}>
                {i}
              </button>
            ))}
          </div>
        )}

        {/* Stake + Add bet + Spin */}
        <div style={{display:'flex',gap:4,alignItems:'center',marginBottom:6}}>
          <div style={{display:'flex',gap:2,overflowX:'auto',flex:1}}>
            {STAKES.map(sv=>(
              <button key={sv} className="rb"
                onClick={()=>setStake(sv)}
                style={{
                  borderRadius:5,padding:'3px 6px',cursor:'pointer',flexShrink:0,
                  border:stake===sv?'1.5px solid #c9a84c':'1.5px solid rgba(255,255,255,.1)',
                  background:stake===sv?'rgba(201,168,76,.18)':'rgba(255,255,255,.04)',
                  color:stake===sv?'#c9a84c':'rgba(255,255,255,.4)',
                  fontSize:10,fontWeight:700,whiteSpace:'nowrap',transition:'all .1s',
                }}>
                ₹{sv>=1000?`${sv/1000}K`:sv}
              </button>
            ))}
          </div>
          <button className="rb" onClick={addBet}
            style={{flexShrink:0,borderRadius:7,padding:'6px 12px',cursor:'pointer',
              border:'1.5px solid rgba(201,168,76,.5)',
              background:'rgba(201,168,76,.15)',color:'#c9a84c',
              fontSize:11,fontWeight:800,whiteSpace:'nowrap',transition:'all .12s'}}>
            + BET
          </button>
        </div>

        {/* Spin button */}
        <button className="rb" onClick={handleSpin}
          disabled={!canSpin||bets.length===0}
          style={{
            width:'100%',padding:'11px 0',borderRadius:10,border:'none',
            cursor:canSpin&&bets.length>0?'pointer':'default',
            fontWeight:900,fontSize:15,transition:'all .15s',
            background:canSpin&&bets.length>0
              ?'linear-gradient(135deg,#c9a84c,#8b6914)'
              :'rgba(255,255,255,.07)',
            color:canSpin&&bets.length>0?'#fff':'rgba(255,255,255,.2)',
            boxShadow:canSpin&&bets.length>0?'0 4px 20px rgba(201,168,76,.35)':'none',
          }}>
          {phase==='spinning'
            ?'🎡 Spinning...'
            :bets.length>0
              ?`🎡 SPIN — ₹${totalStake} total`
              :'Select bets above then SPIN'}
        </button>
      </div>

      {/* Straight num indicator */}
      {activeBetType==='straight'&&(
        <div style={{position:'absolute',bottom:160,right:12,zIndex:20,
          background:straightNum===0?'#166534':RED_NUMS.has(straightNum)?'#b01020':'#111',
          border:'2px solid #c9a84c',borderRadius:10,
          width:36,height:36,display:'flex',alignItems:'center',justifyContent:'center',
          fontWeight:900,fontSize:16,color:'#fff'}}>
          {straightNum}
        </div>
      )}
    </div>
  )
}
